import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import apiClient from '../api/client';
import { CATEGORIES, todayString, TITLE_MAX, CONTENT_MAX, TAGS_MAX, TAG_LENGTH_MAX } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import { PRIMARY } from '../styles/shared';
import { trackEvent } from '../utils/analytics';

// Per-device flag for the `first_entry_created` analytics event. Backend
// doesn't easily know "is this the user's first entry" (entries can be
// deleted, multi-device, etc), so we dedupe client-side. Worst case: a user
// clears localStorage and we double-fire — acceptable for funnel metrics.
const FIRST_ENTRY_FIRED_KEY = 'bragsheet_first_entry_tracked';

// Local-only journal draft (T23): we autosave the in-progress entry to
// localStorage every 1.5s so users don't lose work on accidental navigation
// or browser crashes. Cleared on successful submit; restorable for 24h.
const DRAFT_KEY = 'bragsheet_draft_new_entry';
const AUTOSAVE_DEBOUNCE_MS = 1500;
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function formatSavedTime(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

function formatDraftTimestamp(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Pool of writing prompts (T24). Picked at random on mount; user can cycle.
const WRITING_PROMPTS = [
  'What did you lead today?',
  'What surprised you this week?',
  'What challenged you and how did you respond?',
  'Describe a moment you felt proud.',
  'What did you learn from a recent setback?',
  'Who helped you, and how can you thank them?',
  'What activity gave you the most energy?',
  'If you re-did one thing this week, what would it be?',
];

function pickRandomPrompt(exclude) {
  if (!exclude) return WRITING_PROMPTS[Math.floor(Math.random() * WRITING_PROMPTS.length)];
  const pool = WRITING_PROMPTS.filter(p => p !== exclude);
  return pool[Math.floor(Math.random() * pool.length)];
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
  content: { maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' },
  header: { marginBottom: '2rem' },
  title: { fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '2rem',
  },
  formGroup: { marginBottom: '1.25rem' },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  charCount: { fontSize: '0.8rem', color: '#94a3b8' },
  // M3/M4 (T42): inputs gain minHeight: 44px so they hit comfortable tap
  // target size on touch devices and align with iOS no-zoom (16px font).
  input: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    minHeight: '44px',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    resize: 'vertical',
    minHeight: '200px',
    fontFamily: 'inherit',
    lineHeight: '1.6',
  },
  select: {
    width: '100%',
    padding: '0.65rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    outline: 'none',
    minHeight: '44px',
  },
  tagInput: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    minHeight: '44px',
  },
  tagHint: { fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.3rem' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.6rem',
    backgroundColor: '#f0fdfa',
    color: '#01696F',
    border: '1px solid #99f6e4',
    borderRadius: '999px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  chipRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#01696F',
    fontSize: '0.9rem',
    lineHeight: '1',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
  },
  btnRow: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  // M3/M4 (T42): submit/cancel meet 44px tap target. The desktop padding
  // already produces a ~44px button, but min-height makes it explicit and
  // protects against future font/padding tweaks.
  btn: {
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
    minHeight: '44px',
  },
  primaryBtn: { backgroundColor: PRIMARY, color: '#ffffff' },
  cancelBtn: { backgroundColor: '#ffffff', color: '#64748b', border: '1px solid #e2e8f0' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  error: {
    color: '#ef4444',
    fontSize: '0.875rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  // T27: prominent voice-input row — full button with icon + text label,
  // dedicated placement under the Content textarea. Bigger (24x24+ icon)
  // and visually obvious recording affordance when active (red dot pulse).
  voiceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  voiceBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.55rem 1rem',
    borderRadius: '999px',
    border: '1px solid #99f6e4',
    backgroundColor: '#f0fdfa',
    color: '#01696F',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
  },
  voiceBtnHover: {
    backgroundColor: '#ccfbf1',
    borderColor: '#01696F',
  },
  voiceBtnActive: {
    backgroundColor: '#ef4444',
    borderColor: '#ef4444',
    color: '#ffffff',
    boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.25)',
  },
  voiceBtnActiveHover: {
    backgroundColor: '#dc2626',
    borderColor: '#dc2626',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'inline-block',
    animation: 'bragsheetVoicePulse 1s ease-in-out infinite',
  },
  micNotSupported: {
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '0.25rem',
  },
  speechError: {
    fontSize: '0.8rem',
    color: '#ef4444',
    marginTop: '0.25rem',
  },
  interimText: {
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '0.25rem',
    padding: '0.4rem 0.6rem',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px dashed #e2e8f0',
  },
};

export default function NewEntryPage() {
  const navigate = useNavigate();
  const { isMobile } = useWindowWidth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Academic');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [entryDate, setEntryDate] = useState(todayString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagLoading, setTagLoading] = useState(false);
  const [tagError, setTagError] = useState('');

  // Autosave (T23): tracks the in-flight/saved state of the localStorage draft.
  // `status` is 'idle' | 'saving' | 'saved' | 'failed'. `savedAt` is the ms
  // timestamp of the last successful write — used to render "Saved at h:mm:ss".
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  const [autosaveSavedAt, setAutosaveSavedAt] = useState(null);
  // Restore-draft banner state: populated on mount if a recent draft exists.
  const [draftPrompt, setDraftPrompt] = useState(null);

  // Writing prompts (T24): pick one at random on mount; user can cycle.
  const [writingPrompt, setWritingPrompt] = useState(
    () => WRITING_PROMPTS[Math.floor(Math.random() * WRITING_PROMPTS.length)]
  );

  const { isListening, transcript, isSupported, startListening, stopListening, error: speechError } = useSpeechRecognition();
  const lastTranscriptRef = useRef('');

  useEffect(() => {
    if (transcript && transcript !== lastTranscriptRef.current) {
      lastTranscriptRef.current = transcript;
    }
  }, [transcript]);

  // T23: on mount, look for a recent draft and prompt the user to restore it.
  // Strict-mode runs effects twice in dev; that's fine — we just re-read the
  // same localStorage key. We don't auto-apply the draft so the user keeps
  // control over their fresh-page state.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== 'object') return;
      const age = Date.now() - (draft.savedAt || 0);
      if (age < 0 || age > DRAFT_MAX_AGE_MS) {
        // Stale — clean it up so we don't keep offering it.
        window.localStorage.removeItem(DRAFT_KEY);
        return;
      }
      // Only offer if there's actual content to restore.
      if ((draft.title && draft.title.trim()) || (draft.content && draft.content.trim())) {
        setDraftPrompt(draft);
      }
    } catch {
      // Corrupted draft — drop it.
      try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
    }
  }, []);

  // T23: debounced autosave. After 1.5s of no typing, snapshot the form to
  // localStorage. We avoid saving an empty draft (nothing to recover). The
  // dependency array intentionally excludes the dismissed-banner state so
  // user typing after dismissal still autosaves.
  useEffect(() => {
    // Don't autosave while the restore banner is showing — the form is still
    // in its initial blank state and we'd clobber the existing draft.
    if (draftPrompt) return;
    const hasContent = (title && title.trim()) || (content && content.trim());
    if (!hasContent) return;

    setAutosaveStatus('saving');
    const timer = setTimeout(() => {
      try {
        const payload = {
          title,
          content,
          category,
          tags,
          entryDate,
          savedAt: Date.now(),
        };
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        setAutosaveSavedAt(payload.savedAt);
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('failed');
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [title, content, category, tags, entryDate, draftPrompt]);

  const handleRestoreDraft = () => {
    if (!draftPrompt) return;
    setTitle(draftPrompt.title || '');
    setContent(draftPrompt.content || '');
    if (draftPrompt.category) setCategory(draftPrompt.category);
    if (Array.isArray(draftPrompt.tags)) setTags(draftPrompt.tags);
    if (draftPrompt.entryDate) setEntryDate(draftPrompt.entryDate);
    setAutosaveSavedAt(draftPrompt.savedAt || null);
    setAutosaveStatus(draftPrompt.savedAt ? 'saved' : 'idle');
    setDraftPrompt(null);
  };

  const handleDiscardDraft = () => {
    try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
    setDraftPrompt(null);
  };

  const handleCyclePrompt = () => {
    setWritingPrompt(prev => pickRandomPrompt(prev));
  };

  const handleUsePromptAsStarter = () => {
    if (!writingPrompt) return;
    setContent(prev => {
      if (!prev) return `${writingPrompt}\n`;
      const sep = prev.endsWith('\n') ? '' : '\n';
      return `${prev}${sep}${writingPrompt}\n`;
    });
  };

  const handleToggleRecording = () => {
    if (isListening) {
      stopListening();
      // Append the final transcript to the content
      if (lastTranscriptRef.current.trim()) {
        setContent(prev => {
          const separator = prev.length > 0 && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
          return prev + separator + lastTranscriptRef.current.trim();
        });
        lastTranscriptRef.current = '';
      }
    } else {
      startListening();
    }
  };

  const addTag = (val) => {
    const trimmed = val.trim().toLowerCase().replace(/^#/, '');
    if (!trimmed) { setTagInput(''); return; }
    if (tags.length >= TAGS_MAX) {
      setTagError(`Maximum ${TAGS_MAX} tags allowed`);
      setTagInput('');
      return;
    }
    if (trimmed.length > TAG_LENGTH_MAX) {
      setTagError(`Tags must be ${TAG_LENGTH_MAX} characters or fewer`);
      setTagInput('');
      return;
    }
    if (!tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagError('');
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const handleTagBlur = () => {
    if (tagInput.trim()) addTag(tagInput);
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const suggestTags = async () => {
    if (!content.trim()) return; // no content to analyze yet
    setTagLoading(true);
    setTagError('');
    try {
      const res = await apiClient.post('/api/ai/auto-tag', { content, title, category });
      const suggested = (res.data && res.data.data && res.data.data.tags) ? res.data.data.tags : [];
      // merge without duplicates
      setTags(prev => {
        const merged = [...prev];
        for (const t of suggested) {
          if (!merged.includes(t)) merged.push(t);
        }
        return merged;
      });
    } catch (err) {
      setTagError('Could not suggest tags. You can add them manually.');
    } finally {
      setTagLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiClient.post('/api/entries', {
        title,
        content,
        category,
        tags,
        entryDate,
      });
      // T23: entry persisted server-side — drop the local draft.
      try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      // Fire once per device on the first successful entry create.
      try {
        if (window.localStorage.getItem(FIRST_ENTRY_FIRED_KEY) !== 'true') {
          window.localStorage.setItem(FIRST_ENTRY_FIRED_KEY, 'true');
          trackEvent('first_entry_created');
        }
      } catch (_) {
        // localStorage may be disabled; still emit the event but skip dedupe.
        trackEvent('first_entry_created');
      }
      navigate('/journal');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save entry.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <NavBar />
      {/* T27: keyframes for the recording-dot pulse. Inlined here because */}
      {/* this codebase doesn't use a global CSS file.                      */}
      <style>{`@keyframes bragsheetVoicePulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }`}</style>
      {/* M3 (T42): tighter mobile padding (~1rem) to reclaim the squeezed
          horizontal space that was making the form feel cramped on phones. */}
      <div id="main-content" tabIndex={-1} style={{
        ...styles.content,
        padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
      }}>
        <div style={styles.header}>
          <h1 style={styles.title}>New Journal Entry</h1>
        </div>

        <div style={{
          ...styles.card,
          padding: isMobile ? '1.25rem' : '2rem',
        }}>
          {error && <div style={styles.error} role="alert">{error}</div>}
          {draftPrompt && (
            <div
              role="status"
              style={{
                backgroundColor: '#f0fdfa',
                border: '1px solid #99d5cf',
                color: '#01696F',
                borderRadius: '8px',
                padding: '0.65rem 0.875rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
                fontSize: '0.875rem',
              }}
            >
              <span>
                Resume your draft from {formatDraftTimestamp(draftPrompt.savedAt)}?
              </span>
              <button
                type="button"
                onClick={handleRestoreDraft}
                style={{
                  padding: '0.3rem 0.75rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#01696F',
                  color: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Restore
              </button>
              <button
                type="button"
                onClick={handleDiscardDraft}
                style={{
                  padding: '0.3rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #99d5cf',
                  backgroundColor: '#ffffff',
                  color: '#01696F',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Discard
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span>Title *</span>
                <span style={{ ...styles.charCount, color: title.length > TITLE_MAX - 20 ? '#ef4444' : '#94a3b8' }}>{title.length}/{TITLE_MAX}</span>
              </label>
              <input
                style={styles.input}
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="What happened today?"
                required
                maxLength={TITLE_MAX}
                autoFocus
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
              <div
                aria-live="polite"
                style={{
                  fontSize: '0.8rem',
                  color: autosaveStatus === 'failed' ? '#ef4444' : '#94a3b8',
                  marginTop: '0.3rem',
                  minHeight: '1.1em',
                }}
              >
                {autosaveStatus === 'saving' && 'Saving…'}
                {autosaveStatus === 'saved' && autosaveSavedAt && `Saved at ${formatSavedTime(autosaveSavedAt)}`}
                {autosaveStatus === 'failed' && 'Save failed'}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span>Content *</span>
                <span style={{ ...styles.charCount, color: content.length > CONTENT_MAX - 5000 ? '#ef4444' : '#94a3b8' }}>{content.length.toLocaleString()}/{CONTENT_MAX.toLocaleString()}</span>
              </label>
              <textarea
                style={{ ...styles.textarea, minHeight: isMobile ? '140px' : '200px' }}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Write about your experience, what you learned, how it connects to your goals..."
                required
                maxLength={CONTENT_MAX}
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
              {/* T27: prominent voice input row directly under the textarea. */}
              {/* Wrapped in a div on its own line so it doesn't compete with  */}
              {/* the writing-prompt row below for attention.                  */}
              {isSupported && (
                /* M3 (T42): on mobile, voice row stacks vertically; the
                   button stretches full-width so it's an easy thumb target. */
                <div style={{
                  ...styles.voiceRow,
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                }}>
                  <button
                    type="button"
                    onClick={handleToggleRecording}
                    onMouseEnter={(e) => {
                      const hover = isListening ? styles.voiceBtnActiveHover : styles.voiceBtnHover;
                      e.currentTarget.style.backgroundColor = hover.backgroundColor;
                      if (hover.borderColor) e.currentTarget.style.borderColor = hover.borderColor;
                    }}
                    onMouseLeave={(e) => {
                      const base = isListening ? styles.voiceBtnActive : styles.voiceBtn;
                      e.currentTarget.style.backgroundColor = base.backgroundColor;
                      e.currentTarget.style.borderColor = base.borderColor;
                    }}
                    style={{
                      ...styles.voiceBtn,
                      ...(isListening ? styles.voiceBtnActive : {}),
                      minHeight: '44px',
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                    }}
                    aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                    aria-pressed={isListening}
                  >
                    {isListening ? (
                      <span style={styles.recordingDot} aria-hidden="true" />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    )}
                    <span>{isListening ? 'Recording… tap to stop' : 'Voice input'}</span>
                  </button>
                </div>
              )}
              {!isSupported && (
                <div style={styles.micNotSupported}>
                  Voice input is not supported in this browser. Try Chrome or Edge.
                </div>
              )}
              {speechError && <div style={styles.speechError}>{speechError}</div>}
              {isListening && transcript && (
                <div style={styles.interimText}>
                  Hearing: "{transcript}"
                </div>
              )}
              {/* M3 (T42): on mobile, prompt text + action buttons stack
                  vertically so they don't compete in a single cramped row. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '0.4rem' : '0.5rem',
                  flexWrap: 'wrap',
                  marginTop: '0.75rem',
                  fontSize: '0.85rem',
                  color: '#01696F',
                  opacity: 0.85,
                }}
              >
                <span style={{ flex: '1 1 auto', minWidth: '180px' }}>
                  Prompt: {writingPrompt}
                </span>
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}>
                  <button
                    type="button"
                    onClick={handleCyclePrompt}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#01696F',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      padding: '0.35rem 0.35rem',
                      minHeight: isMobile ? '44px' : 'auto',
                      textDecoration: 'underline',
                    }}
                    aria-label="Show another writing prompt"
                  >
                    ↻ Another prompt
                  </button>
                  <button
                    type="button"
                    onClick={handleUsePromptAsStarter}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#01696F',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      padding: '0.35rem 0.35rem',
                      minHeight: isMobile ? '44px' : 'auto',
                      textDecoration: 'underline',
                    }}
                    aria-label="Use this prompt as a starter in the content field"
                  >
                    Use as starter
                  </button>
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}><span>Category</span></label>
              <select
                style={styles.select}
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <span>Tags</span>
                <span style={{ ...styles.charCount, color: tags.length >= TAGS_MAX ? '#ef4444' : '#94a3b8' }}>{tags.length}/{TAGS_MAX}</span>
              </label>
              <input
                style={styles.tagInput}
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={(e) => { handleTagBlur(); e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                disabled={tags.length >= TAGS_MAX}
                placeholder={tags.length >= TAGS_MAX ? 'Maximum tags reached' : 'Add a tag and press Enter or comma'}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.3rem' }}>
                <span style={styles.tagHint}>Press Enter or comma to add a tag</span>
                <button
                  type="button"
                  onClick={suggestTags}
                  disabled={tagLoading || !content.trim()}
                  // T26: ghost/outlined style — transparent fill, 1px teal
                  // border, teal text. Hover lifts to a light teal background.
                  // Matches the visual weight of other secondary controls.
                  onMouseEnter={(e) => {
                    if (tagLoading || !content.trim()) return;
                    e.currentTarget.style.backgroundColor = '#f0fdfa';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  style={{
                    padding: '0.4rem 0.85rem',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    borderRadius: '999px',
                    backgroundColor: 'transparent',
                    color: '#01696F',
                    border: '1px solid #01696F',
                    cursor: tagLoading || !content.trim() ? 'not-allowed' : 'pointer',
                    opacity: tagLoading || !content.trim() ? 0.6 : 1,
                    width: 'auto',
                    marginTop: '0',
                    transition: 'background-color 0.15s',
                  }}
                >
                  {tagLoading ? 'Suggesting...' : 'Suggest Tags'}
                </button>
              </div>
              {tagError && (
                <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  {tagError}
                </div>
              )}
              {tags.length > 0 && (
                <div style={styles.chips}>
                  {tags.map(tag => (
                    <span key={tag} style={styles.chip}>
                      #{tag}
                      <button
                        type="button"
                        style={styles.chipRemove}
                        onClick={() => removeTag(tag)}
                        aria-label={`Remove tag ${tag}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}><span>Entry Date</span></label>
              <input
                style={styles.input}
                type="date"
                value={entryDate}
                onChange={e => setEntryDate(e.target.value)}
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* M3 (T42): on mobile, stack Cancel/Save full-width so each is
                an obvious tap target and Save sits prominently at the bottom. */}
            <div style={{
              ...styles.btnRow,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? '0.5rem' : '0.75rem',
            }}>
              <button
                type="button"
                style={{
                  ...styles.btn,
                  ...styles.cancelBtn,
                  width: isMobile ? '100%' : 'auto',
                }}
                onClick={() => navigate('/journal')}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...styles.btn,
                  ...styles.primaryBtn,
                  ...(loading ? styles.btnDisabled : {}),
                  width: isMobile ? '100%' : 'auto',
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Entry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
